import { useEffect } from "react";
import type { ReactNode } from "react";
import { IconClose } from "./icons";

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Optional footer (e.g. action buttons). */
  footer?: ReactNode;
  /** Widen the modal for larger forms. */
  wide?: boolean;
  /** Extra-wide — for forms with many fields or line-item tables. */
  xwide?: boolean;
}

// Lightweight, dependency-free modal — overlay + card, closes on Escape or
// backdrop click. Used for the create/edit forms across the admin screens.
export const Modal = ({
  open,
  title,
  onClose,
  children,
  footer,
  wide,
  xwide,
}: ModalProps) => {
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`modal${xwide ? " modal--xwide" : wide ? " modal--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__head">
          <h2 className="modal__title">{title}</h2>
          <button
            type="button"
            className="icon-btn"
            aria-label="Close"
            onClick={onClose}
          >
            <IconClose />
          </button>
        </div>

        <div className="modal__body">{children}</div>

        {footer ? <div className="modal__foot">{footer}</div> : null}
      </div>
    </div>
  );
};
